import { Overlay, OverlayContainer, ViewportRuler } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { Component, Injector, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router, RoutesRecognized } from '@angular/router';
import { SetFormAction } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import { GoogleAnalyticsService } from '@app/finxone-web-frontend/app/lib/services/google-analytics/google-analytic.service';
import { StyleServiceService } from '@app/finxone-web-frontend/app/lib/services/style-service/style-service.service';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import {
  checkZonePagesListForNexPageUrls,
  handleBackgroundShape,
  isFormWidget,
  isLastPage,
} from '@app/finxone-web-frontend/app/lib/utils/utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  BaseCssProperties,
  ProviderClass,
  UiPageConfig,
  WidgetNames,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Subject, filter, firstValueFrom, pairwise, takeUntil } from 'rxjs';
import { OVERLAY_ACTIVE_TYPE, OverlayWidgetComponent } from '../overlay/overlay';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-onboarding-next-back-button',
  templateUrl: './onboarding-next-back-button.component.html',
  styleUrl: './onboarding-next-back-button.component.scss',
  providers: [{ provide: OVERLAY_ACTIVE_TYPE, useValue: false }],
})
export class OnboardingNextBackButtonComponent
  extends OverlayWidgetComponent<OnboardingNextBackButtonComponent>
  implements OnInit
{
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public icon: string;
  public buttonType: string;
  public iconSize: baseCssConfigStyle;
  private backgroundShape: 'circle' | 'square' | 'rectangle' | 'none';
  private currentZoneFromRoute: {
    zone: string;
    pageUrl: string;
    onboardingOrgId?: string;
  };
  public activePageIndex!: number;
  public zonePagesList: UiPageConfig[];
  public pagesMapping: { [key: string]: Record<string, string | boolean> } = {};
  public redirectionPath: string;
  private roleName: string;
  private subscription: string;
  private prevUrl: string;
  public text = '';
  public globalStylingClass = '';
  public isAllowedGlobalStyling = false;
  public renderedContent = '';

  constructor(
    public override dataIn: ProviderClass<any>,
    private store: Store,
    public override configService: ConfigService,
    private route: ActivatedRoute,
    private styleServiceService: StyleServiceService,
    private formSubmissionService: FormSubmissionService,
    private _router: Router,
    overlay: Overlay,
    injector: Injector,
    _viewportRuler: ViewportRuler,
    _platform: Platform,
    _overlayContainer: OverlayContainer,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    private accountService: AccountService,
    private googleAnalyticService: GoogleAnalyticsService,
  ) {
    super(
      dataIn,
      overlay,
      injector,
      OnboardingNextBackButtonComponent,
      _viewportRuler,
      _platform,
      _overlayContainer,
      configService,
    );
    this.getPrevUrl();
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params: ParamMap) => {
      this.currentZoneFromRoute = this.styleServiceService.parseRouteParams(params);
      this.getCurrentPageIndexByPageUrl();
      this.roleName = this.styleServiceService.parseRouteParams(params).roleName;
      this.subscription = this.styleServiceService.parseRouteParams(params).subscription;
    });
    this.getZonePagesListData();
  }

  ngOnInit() {
    if (this.dataIn != undefined) {
      this.attri = this.dataIn.attri;
      this.icon = this.attri.widgetProperties?.icon ?? '';
      this.buttonType = this.attri?.['buttonType'] ?? '';
      this.onLoadWidgetContent();

      this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
        BaseCssProperties.BOX_SHADOW,
        BaseCssProperties.TEXTALIGN,
        BaseCssProperties.BACKGROUND_COLOR,
        BaseCssProperties.COLOR,
        BaseCssProperties.V_ALIGN,
        BaseCssProperties.BORDER,
        BaseCssProperties.MARGIN,
        BaseCssProperties.PADDING,
        BaseCssProperties.TEXTALIGN,
        BaseCssProperties.FONT_SIZE,
        BaseCssProperties.TEXT_ITALIC,
        BaseCssProperties.TEXT_UNDERLINE,
        BaseCssProperties.TEXT_BOLD,
        BaseCssProperties.TEXT_FONT_STYLE,
      ]);
      this.iconSize = this.configService.getSpecificBaseCssStyle(this.attri, [BaseCssProperties.ICON_SIZE]);
      this.backgroundShape = this.attri.widgetProperties['backgroundShape'];
      this.contentStyle = handleBackgroundShape(this.backgroundShape, this.contentStyle);
      this.getGlobalStyling();
    }
  }

  private onLoadWidgetContent(): void {
    this.text = this.dataIn.attri?.widgetProperties?.textContent ?? '';
    const template = Handlebars.compile(this.text);
    buildHandleBarModel(
      this.store,
      this.text,
      this.countriesCurrenciesService,
      this.iconCacheService,
      this.accountService,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.renderedContent = filterIfHandlebarNotRendered(template(model));
      });
  }

  private getGlobalStyling() {
    if (
      this.dataIn.attri.widgetProperties?.['globalStylingClass'] &&
      this.dataIn.attri.widgetProperties?.['globalStyling']
    ) {
      this.globalStylingClass = this.dataIn.attri.widgetProperties?.['globalStylingClass'] ?? '';
      this.isAllowedGlobalStyling = this.dataIn.attri.widgetProperties?.['globalStyling'] ?? false;
    }
  }

  pageAction(event: any) {
    this.googleAnalyticService.sendEvent('onboarding');
    if (event?.['action'] === 'back') {
      if ((this.activePageIndex === 0 && !this.prevUrl) || this.prevUrl?.includes('feature-redirect')) {
        this._router.navigate(['/']);
      } else {
        this.handlePrevAction();
      }
    }
    if (event?.['action'] === 'next') {
      this.handleNextAction();
    }
  }

  private getPrevUrl() {
    this._router.events
      .pipe(
        filter((evt: any) => evt instanceof RoutesRecognized),
        pairwise(),
      )
      .subscribe((event: any[]) => {
        this.prevUrl = event[0].urlAfterRedirects;
      });
  }

  public handlePrevAction() {
    if (this.activePageIndex === undefined) {
      return;
    }
    const prevPageUrl = this.configService.getPageIndexPageUrl(
      this.currentZoneFromRoute.zone,
      this.activePageIndex - 1,
    );
    if (prevPageUrl) {
      const prevPageUrlComplete = this.updateRedirectionPath(prevPageUrl);
      this.formSubmissionService.resetCurrentPageFormGroups();
      this._router.navigate([prevPageUrlComplete]);
    }
  }

  private getZonePagesListData() {
    this.configService.getCurrentRoleZonePagesConfig().subscribe((response: UiPageConfig[]) => {
      this.zonePagesList = response;
      this.checkPagesWithForms(this.zonePagesList);
    });
  }

  public handleNextAction() {
    if (this.activePageIndex === undefined) {
      return;
    }
    const formValid = this.formSubmissionService.currentPageIsValid();
    const { nextPageUrl, pageIndex } = this.getNextPageUrl();
    if ((!this.checkZonePagesAndSubmit() || !nextPageUrl) && formValid) {
      this.updateFormState(pageIndex ? pageIndex - 1 : this.activePageIndex, '');
      this.submitForm(nextPageUrl ? this.updateRedirectionPath(nextPageUrl) : '');
      return;
    }
    const formWidgetCount = isFormWidget(this.activePageIndex, this.zonePagesList);

    if (nextPageUrl && formValid) {
      this.updateRedirectionPath(nextPageUrl);
    }

    this.updateFormState(this.activePageIndex, nextPageUrl ? this.redirectionPath : '');
    if (!formWidgetCount && this.redirectionPath) {
      this._router.navigate([this.redirectionPath]);
    }
  }

  private updateFormState(pageIndex: number, nextPageUrl: string) {
    this.store.dispatch(
      new SetFormAction({
        type: isFormWidget(this.activePageIndex, this.zonePagesList) ? 'submitStep' : '',
        nextPageUrl: nextPageUrl,
        isLastPage: isLastPage(pageIndex, this.zonePagesList),
        formData:
          this.formSubmissionService.currentPageFormGroups[this.formSubmissionService.currentPageIndex].value,
      }),
    );
  }

  private submitForm(nextPageUrl = '') {
    const formDataSnapshot = this.store.selectSnapshot(FormActionState.getFormActionState);
    const formData = formDataSnapshot?.response?.formData;
    if (formData) {
      this.formSubmissionService.submitFormPayload(
        '',
        formData,
        this.roleName,
        this.subscription,
        this.currentZoneFromRoute?.onboardingOrgId,
        nextPageUrl,
      );
    }
  }
  checkZonePagesAndSubmit(): boolean {
    const startIndex = Number(this.activePageIndex) + 1;

    for (let index = startIndex; index < this.zonePagesList.length; index++) {
      if (this.pagesMapping[index]?.['hasFormWidget']) {
        return true;
      }
    }

    return false;
  }

  private updateRedirectionPath(newPath: string): string {
    this.redirectionPath = this.updateRedirectPath(newPath);
    return this.redirectionPath;
  }

  private getNextPageUrl() {
    const nextPageIndex = this.activePageIndex + 1;
    let nextPageUrl: string | undefined;
    let pageIndex: number | undefined;
    if (this.pagesMapping[nextPageIndex]?.['isVerificationPage']) {
      nextPageUrl = checkZonePagesListForNexPageUrls(
        nextPageIndex,
        this.zonePagesList,
        this.pagesMapping,
      )?.url;
      pageIndex = checkZonePagesListForNexPageUrls(
        nextPageIndex,
        this.zonePagesList,
        this.pagesMapping,
      )?.pageIndex;
    } else {
      nextPageUrl = this.zonePagesList[nextPageIndex]?.url ?? '/';
    }
    return { nextPageUrl, pageIndex };
  }
  private checkZonePagesListForNexPageUrls(startIndex: number) {
    for (let index = startIndex; index < this.zonePagesList.length; index++) {
      if (!this.pagesMapping[index]?.['isVerificationPage']) {
        const url = this.zonePagesList[index]?.url;
        const pageIndex = index;

        return {
          url,
          pageIndex,
        };
      }
    }

    return undefined;
  }

  checkCurrentPageValidation(nextPageUrl?: string) {
    if (!this.formSubmissionService.allPagesAreValid()) {
      this.updateFormState(this.activePageIndex, '');
    } else {
      this.updateFormState(this.activePageIndex, nextPageUrl ?? '');
    }
  }

  private checkIfPageisEmailOrPhoneVerificationPage(page: UiPageConfig): boolean {
    return !!page.widgetLayouts.find((widget) => widget.name === WidgetNames.OTP_VERIFICATION);
  }

  checkPagesWithForms(zonePagesList: UiPageConfig[]) {
    zonePagesList.forEach((page, index) => {
      const hasFormWidget = isFormWidget(index, zonePagesList);
      const isVerificationPage = this.checkIfPageisEmailOrPhoneVerificationPage(page);
      this.pagesMapping[index] = {
        page_url: page.url,
        hasFormWidget: hasFormWidget,
      };
      if (isVerificationPage) {
        this.pagesMapping[index] = {
          ...this.pagesMapping[index],
          isVerificationPage: isVerificationPage,
        };
      }
    });
  }

  async getCurrentPageIndexByPageUrl() {
    const result = await firstValueFrom(
      this.configService.getPageUrlPageIndex(
        this.currentZoneFromRoute.zone,
        this.currentZoneFromRoute.pageUrl,
      ),
    );

    if (result !== undefined) {
      this.activePageIndex = result;
    }
  }

  private updateRedirectPath(newPath: string): string {
    const path = location.pathname.split('/');
    path[3] = newPath;
    return path.join('/');
  }
}
